import axios from "axios";
import {Api} from "@/shared/services/api";
import notificationService from "@/shared/services/NotificationService";
import VehicleRegistrationInquiry, {VehicleRegistrationInquiryItem} from "@/shared/model/VehicleRegistrationInquiry";

/** Vorlage einer Mail-Art (Betreff und Text) am Versicherer. */
export interface InquiryTemplate {
  mailTypeId?: string;
  mailTypeText?: string;
  subject?: string;
  body?: string;
}

export class ApiRegistrationInquiry extends Api {
  /** Anfragen mit den angegebenen Status (kommagetrennt, z. B. "Draft,Failed"); ohne Angabe: Entwurf, Freigegeben, Fehlgeschlagen. */
  public async list(status?: string): Promise<VehicleRegistrationInquiry[] | undefined> {
    try {
      const resp = await axios.get("/VehicleRegistrationInquiryList", {
        ...this.getConfigApi(),
        params: status ? {status: status} : undefined,
      });
      if (resp.data.successful) {
        return resp.data.result ?? [];
      }
      notificationService.error(resp.data.message);
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      notificationService.error(error.response?.status + ": " + error.response?.statusText);
    }
    return undefined;
  }

  public async load(id: string): Promise<{ inquiry: VehicleRegistrationInquiry, items: VehicleRegistrationInquiryItem[] } | undefined> {
    const resp = await this.post("/VehicleRegistrationInquiryLoadRequest", {id: id});
    if (resp.successful && resp.inquiry) {
      return {inquiry: resp.inquiry, items: resp.items ?? []};
    }
    return undefined;
  }

  public async update(id: string, recipients: string, subject: string, bodyText: string, attachExcel: boolean): Promise<boolean> {
    const resp = await this.post("/VehicleRegistrationInquiryUpdateRequest", {
      id: id, recipients: recipients, subject: subject, bodyText: bodyText, attachExcel: attachExcel
    });
    return resp.successful;
  }

  /** Gibt einen Entwurf frei; der Versand erfolgt durch den Hintergrunddienst. Liefert auch einen Hinweis (z. B. entfernte, inzwischen gelöste Zulassungen). */
  public async approve(id: string): Promise<{ successful: boolean, message?: string }> {
    const resp = await this.post("/VehicleRegistrationInquiryApproveRequest", {id: id}, false);
    if (!resp.successful && resp.message && !resp.message.startsWith("Alle angefragten")) {
      notificationService.error(resp.message);
    }
    return {successful: resp.successful, message: resp.message};
  }

  /** Alle Vorlagen (Betreff und Text je Mail-Art) eines Versicherers. */
  public async templates(insurerPersonId: string): Promise<InquiryTemplate[] | undefined> {
    const resp = await this.post("/VehicleRegistrationInquiryTemplatesLoadRequest", {insurerPersonId: insurerPersonId});
    return resp.successful ? (resp.items ?? []) : undefined;
  }

  /** Speichert Betreff und Text der übergebenen Mail-Arten; bestehende Entwürfe bleiben unverändert. */
  public async saveTemplates(insurerPersonId: string, items: InquiryTemplate[]): Promise<boolean> {
    const resp = await this.post("/VehicleRegistrationInquiryTemplatesSaveRequest", {insurerPersonId: insurerPersonId, items: items});
    return resp.successful;
  }

  /** Ändert die Art eines Entwurfs; Betreff und Text werden aus der Vorlage der neuen Art neu gebildet. */
  public async changeMailType(id: string, mailTypeId: string): Promise<boolean> {
    const resp = await this.post("/VehicleRegistrationInquiryChangeTypeRequest", {id: id, mailTypeId: mailTypeId});
    return resp.successful;
  }

  /** Legt manuell einen Entwurf für genau eine unbekannte Zulassung an; liefert die ID der neuen Anfrage. */
  public async create(vehicleRegistrationId: string, mailTypeId: string): Promise<string | undefined> {
    const resp = await this.post("/VehicleRegistrationInquiryCreateRequest", {vehicleRegistrationId: vehicleRegistrationId, mailTypeId: mailTypeId});
    return resp.successful ? resp.id : undefined;
  }

  public async discard(id: string): Promise<boolean> {
    const resp = await this.post("/VehicleRegistrationInquiryDiscardRequest", {id: id});
    return resp.successful;
  }
}

const apiRegistrationInquiry = new ApiRegistrationInquiry();

export default apiRegistrationInquiry;
